import {
  IndicatorByService,
  IndicatorSectionKey,
  IndicatorWithUpdate,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import {
  IndicatorDetails,
  SectionIndicators,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';

export interface Report {
  beneficiaryDescription: '';
  budget: {
    current: string; // '1000.00'
    expenses: string;
    notes: number | string;
    variance: string; // '0%'
  }[];
  costInitialDirectCash: string; //"1000.00"
  costInitialDirectInKind: string;
  // "3000.00"
  costInitialIndirectCash: string; // "2000.00"
  costInitialIndirectInKind: string; // "0"
  customQuestionResponses: null;
  demographics: string[];
  description: string;
  donorsClaim: {
    catalyzed: string; // '6000.00'
    contribution: string; // '0.00'
    impact: string;
    indicatorId: number;
    total: string; // '6000.00'
  }[];
  endDate: string; // "30 September 2021"
  funderRatingCategories: [];
  id: number;
  impactModel: {
    [id: number]: {
      name: string;
      data: {
        classSubtitle: string; // '-'
        classTitle: string;
        classification: number;
        description: string;
        sources: string;
        successCriteria: string;
        title: string;
        variance: string; // '0.00'
      }[];
    };
  };
  indicatorList: string[];
  interventionDosageLevel: null;
  interventionDosageNumber: null;
  interventionDosageType: null;
  lessonLearned: string;
  location: string;
  mostRecent: object;
  name: string;
  portionFunded: string; // '0.07'
  primaryInterventionDescription: null;
  primaryInterventionSubtypes: object;
  primaryInterventionType: object;
  programManager: null;
  programName: string;
  services: [];
  socialImpactDelivered: string;
  stage: string;
  startDate: string; // '01 October 2020'
  strategies: [];
  successStory: string;
  successStoryImages: string[]; // urls
  tags: string;
  taxonomySubjectId: null;
  taxonomySubjectLevel: object;
  total: string; // '6000.00'
  updatedAt: string; // '11/20/2024, 09:28'
  updatedBy: string;
  year: number;
  isLegacy?: boolean;
  cnOptedIn?: boolean;
}

export interface ImpactReceipt {
  accountName: string;
  allocation: {
    fundersClaim: string;
    tier: 'Incremental' | 'Significant' | 'Foundational';
  };
  cashAmount: number;
  donorFullName: string;
  goodsAmount: number;
  granteeFullName: string;
  invested: number;
  logo: null;
  portfolioAccountName: string;
  report: Report;
  servicesAmount: number;
  tasks: [];
  volunteerismAmount: number;
  legacy?: {
    donorsClaim: {
      indicatorId: number;
      indicatorName: string;
      forecast: number;
      successCriteria: string;
    }[];
    demographics: {
      category: string;
      group: string;
      subGroup: string;
      served: number;
    }[];
    impactModel: {
      stageId: number;
      forecast: string;
      successCriteria: string;
      sourcesAndAssumptions: string;
      dataQuality: number;
      measurement: string;
      measurementCategory: string;
      variance: string;
    }[];
  };
}

export const convertIndicatorDetailsById = (
  impactDetails: SectionIndicators,
) => {
  const result: { [id: number]: IndicatorDetails } = {};
  Object.values(impactDetails).forEach(x => {
    x.forEach(y => {
      result[y.indicatorId] = y;
    });
  });
  return result;
};

type CombineIndicator = IndicatorDetails & IndicatorWithUpdate;
export type CombineSectionIndicators = {
  [x in IndicatorSectionKey]: CombineIndicator[];
};

export const combineImpactSummaryAndDetails = (
  impactSummary: IndicatorByService | undefined,
  impactDetails: SectionIndicators | undefined,
): CombineSectionIndicators => {
  const result: CombineSectionIndicators = {
    capacityDevelopment: [],
    reach: [],
    succeed: [],
    catalyzed: [],
    learn: [],
    act: [],
  };
  if (!impactSummary || !impactDetails) return result;

  const i = convertIndicatorDetailsById(impactDetails);
  Object.entries(impactSummary).forEach(([key, value]) => {
    if (!Array.isArray(value)) return;
    value.forEach(y => {
      const indicator = i[y.id];
      if (!indicator) return;
      result[key].push({
        ...indicator,
        ...y,
      });
    });
  });

  return result;
};
